import type { ProductDBSource } from '@partful/shared-types';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Toast } from 'src/components/Toast';
import { getProductFiles } from 'src/services/api';
import { FileUploadStatusMap, useUploadFiles } from '../../hooks/useUploadFiles';
import { Dropzone } from './Dropzone';
import { isTenantUploadDBItem, mapSrcToProductFile, ProductFileData, TenantUploadDBItem } from './mapSrcToProductFiles';
import { UploadProgressModal } from './ProgressModal/UploadProgressModal';
import { UploadedFiles } from './UploadedFiles/UploadedFiles';
import { FileDetailsUploadModal } from './UploadModal/FileDetailsUploadModal';

export type PresignedUrl = {
  url: string;
  name: string;
};

const uploadStates = {
  INITIAL: 'initial',
  UPLOADING: 'uploading',
  DONE: 'done',
  ERROR: 'error',
} as const;

export type UploadState = (typeof uploadStates)[keyof typeof uploadStates];

type BasicProductData = Pick<ProductDBSource, 'productId' | 'name'>;

export type FileType = 'bom' | 'pricing' | 'other';

/**
 * File to upload (local state)
 */
export type ProductFile = {
  file: File;
  name: string;
  uploadState?: UploadState;
  presignedUrl?: PresignedUrl;
  products: BasicProductData[] | undefined;
  description: string | undefined;
  type?: FileType;
  size: number;
  createdAt?: string | number;
  updatedAt?: string | number;
  createdBy?: string;
  updatedBy?: string;
};

export const PRODUCT_FILES_QUERY_KEY = 'product-files';

export function Uploader() {
  const [productFiles, setProductFiles] = useState<ProductFile[]>([]);
  const [isFileDetailsUploadModalOpen, setIsFileDetailsUploadModalOpen] = useState(false);
  const [isUploadProgressModalOpen, setIsUploadProgressModalOpen] = useState(false);
  const [initNewFiles, setInitNewFiles] = useState<ProductFile[]>([]);

  const { t } = useTranslation('uploads');

  const { mutate, filesUploadStatus, isError, isSuccess } = useUploadFiles(productFiles);

  const { data: productFilesData = [] } = useQuery([PRODUCT_FILES_QUERY_KEY], getProductFiles);

  const filesWithUploadState = productFiles.map((file) => ({
    ...file,
    uploadState: getUploadState(filesUploadStatus, file),
  }));

  const filesToUpload = filesWithUploadState.filter((file) => file.uploadState === 'initial');

  const uploadedFiles: ProductFileData[] = productFilesData
    .filter(isTenantUploadDBItem)
    .map(/* istanbul ignore next */ (file: TenantUploadDBItem) => mapSrcToProductFile(file));

  const uploadFiles = () => {
    flushSync(() => {
      setInitNewFiles(filesToUpload);
      setIsFileDetailsUploadModalOpen(false);
      setIsUploadProgressModalOpen(true);
      mutate();
    });
  };

  const closeAndClearFileDetails = () => {
    setProductFiles([]);
    setIsFileDetailsUploadModalOpen(false);
  };

  /* istanbul ignore next */
  const closeUploadProgressModal = () => {
    setIsUploadProgressModalOpen(false);
  };

  const openFileDetailsUploadModal = () => {
    setIsFileDetailsUploadModalOpen(true);
  };

  return (
    <>
      <Dropzone
        existingFiles={uploadedFiles}
        setProductFiles={setProductFiles}
        openModal={openFileDetailsUploadModal}
      />
      <UploadedFiles uploadedFiles={uploadedFiles} />
      <FileDetailsUploadModal
        productFiles={filesWithUploadState}
        setProductFiles={setProductFiles}
        uploadFiles={uploadFiles}
        isOpen={isFileDetailsUploadModalOpen}
        onClose={closeAndClearFileDetails}
      />
      <UploadProgressModal
        isOpen={isUploadProgressModalOpen}
        close={closeUploadProgressModal}
        filesUploadStatus={filesUploadStatus}
        initNewFiles={initNewFiles}
      />
      <Toast
        dependency={isError}
        severity="error"
        title={t('status.error.uploadErrorTitle')}
        message={t('status.error.problemUploadingFile', { count: productFiles.length })}
      />
      <Toast
        dependency={isSuccess}
        severity="success"
        title={t('status.success.uploadSuccessful')}
        message={t('status.success.filesSuccessfullyUploaded', { count: productFiles.length })}
      />
    </>
  );
}

function getUploadState(statusMap: FileUploadStatusMap, file: ProductFile): UploadState {
  const fileStatus = statusMap.get(file.name);
  const { INITIAL, DONE, ERROR, UPLOADING } = uploadStates;
  if (!fileStatus) return INITIAL;
  /* istanbul ignore next */
  if (fileStatus.isError) return ERROR;
  /* istanbul ignore next */
  if (fileStatus.isSuccess) return DONE;
  /* istanbul ignore next */
  if (fileStatus.percentage > 0 && fileStatus.percentage < 100) return UPLOADING;
  return INITIAL;
}
